import { cn } from '@/helpers/classnames';
import IconButton from '../core/IconButton';
import { IconTrash } from '@/icons';
import { useDisplayedTicketStatus } from '@/hooks/useDisplayedTicketStatus';
import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Tooltip } from 'react-tooltip';
import {
  type IssuedTicketFindResultDTO,
  type IssuedTicketRevokeDTO,
  useRevokeIssuedTicket,
} from '@/api/trafikportalen';
import { formatDate } from '@/helpers/formatting.ts';
import { toast } from '@/hooks/useToast.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { useTenantID } from '@/hooks/useTenantID.tsx';
import Button from '../core/Button.tsx';
import { CancelIssuedTicketDialog } from './CancelIssuedTicketDialog';
import { invalidateIssuedTicketQueries } from '@/helpers/issuedTicketQueries';
import { issuedTicketRevokeReasonTitle } from '@/helpers/issuedTicketRevokeReason';
import {
  canCancelTicket,
  ongoingActivationEnd,
  periodTicketCancelMessages,
  showsActivationsLeft,
} from '@/helpers/ticket.ts';
import { usePeriodTicketCancelState } from '@/hooks/usePeriodTicketCancelState';
import { useGetDepartmentDisplayName } from '@/hooks/useGetStudentCollection';
import { useTicketClassLabel } from '@/hooks/useTicketClassLabel';
import { useTicketBobcatData } from '@/stores/ticketBobcatData';

// Virtualizer rows are transformed inside an overflow scroller, so this
// tooltip is portaled. `--rt-opacity` overrides the library default of 0.9.
const periodCancelTooltipStyle = {
  maxWidth: 'min(24rem, calc(100vw - 2rem))',
  whiteSpace: 'normal',
  '--rt-opacity': 1,
} as CSSProperties;

interface Props {
  ticket: IssuedTicketFindResultDTO;
  showTravellerName?: boolean;
}

export const TicketListItem = ({ ticket, showTravellerName = true }: Props) => {
  const tenantId = useTenantID();
  const ticketStatus = useDisplayedTicketStatus(ticket);
  const { formatMessage } = useIntl();
  const revokeIssuedTicket = useRevokeIssuedTicket();
  const queryClient = useQueryClient();
  const getDepartmentDisplayName = useGetDepartmentDisplayName();
  const departmentDisplayName = getDepartmentDisplayName(ticket.department_id);
  const getTicketClassLabel = useTicketClassLabel();

  const [cancelOpen, setCancelOpen] = useState<boolean>(false);

  const {
    id,
    created_at,
    traveller_first_name,
    traveller_last_name,
    traveller_id,
    recipient_name,
  } = ticket;

  const bobcatData = useTicketBobcatData(id);

  const travellerName = useMemo(() => {
    if (recipient_name) {
      return recipient_name;
    }

    if (!traveller_first_name && !traveller_last_name) {
      return formatMessage({ defaultMessage: 'Anonym resenär' });
    }

    return `${traveller_first_name ?? ''}${traveller_last_name ? ` ${traveller_last_name}` : ''}`;
  }, [
    traveller_first_name,
    traveller_last_name,
    formatMessage,
    recipient_name,
  ]);

  const periodCancel = usePeriodTicketCancelState(ticket);
  const cancelReason = formatMessage(periodTicketCancelMessages.reason);
  const cancelTooltipId = `cancel-ticket-tooltip-${id}`;
  const cancelDescriptionId = `cancel-ticket-${id}`;

  const startCancel = useCallback(() => {
    setCancelOpen(true);
  }, []);

  const onCancel = useCallback(() => {
    setCancelOpen(false);
  }, []);

  const revokeReasonTitle = useMemo(
    () => issuedTicketRevokeReasonTitle(formatMessage, ticket),
    [formatMessage, ticket],
  );

  const onCancelConfirm = useCallback(
    (data: IssuedTicketRevokeDTO) => {
      revokeIssuedTicket.mutate(
        { id, data },
        {
          onSuccess: async () => {
            toast.success(
              {
                title: formatMessage({
                  defaultMessage: 'Biljetten avslutades',
                }),
              },
              { autoClose: 3000 },
            );
            await invalidateIssuedTicketQueries(queryClient, traveller_id);
            onCancel();
          },
          onError: (error) => {
            console.error('Error: ', error);
            toast.error(
              {
                title: 'Ett fel inträffade',
                description:
                  'Försök spara igen senare, eller vänligen kontakta support.',
              },
              { autoClose: 3000 },
            );
          },
        },
      );
    },
    [
      revokeIssuedTicket,
      id,
      formatMessage,
      queryClient,
      traveller_id,
      onCancel,
    ],
  );

  return (
    <div key={id} className="relative @container">
      <Button
        to="/$tenantId/tickets/$ticketId"
        params={{ tenantId: tenantId, ticketId: id }}
        className="h-fit w-full flex-col items-start justify-start px-4 py-2 text-left font-normal @2xl:flex-row @2xl:gap-0 @2xl:pr-14"
      >
        <div className="flex w-auto flex-col @2xl:w-1/3">
          <TicketInfo
            value={created_at ? formatDate(created_at) : ''}
            className="text-text-700"
          />
          <div className="flex min-w-0 max-w-full flex-wrap gap-x-2">
            <TicketInfo
              value={getTicketClassLabel(ticket)}
              className="min-w-0 max-w-full text-text-900"
            />
            {bobcatData && showsActivationsLeft(bobcatData) ? (
              <TicketInfo
                value={formatMessage(
                  {
                    defaultMessage:
                      '({count, plural, one {# aktivering} other {# aktiveringar}} kvar)',
                  },
                  { count: bobcatData.activations_left },
                )}
                className="whitespace-nowrap text-text-700"
              />
            ) : null}
            {bobcatData &&
            showsActivationsLeft(bobcatData) &&
            ongoingActivationEnd(bobcatData) ? (
              <TicketInfo
                value={formatMessage({ defaultMessage: 'Aktivering pågår' })}
                className="whitespace-nowrap text-text-700"
              />
            ) : null}
          </div>
        </div>
        <div className="flex w-full flex-wrap items-start gap-5 @2xl:w-2/3 @2xl:flex-nowrap">
          {showTravellerName || departmentDisplayName ? (
            <div className="flex flex-col overflow-hidden @2xl:ml-2 @2xl:w-3/5">
              <TicketInfo
                value={
                  showTravellerName ? travellerName : departmentDisplayName
                }
                description={
                  showTravellerName ? departmentDisplayName : undefined
                }
              />
            </div>
          ) : null}
          <div className="ml-auto flex flex-col @2xl:w-2/5">
            <TicketInfo
              value={ticketStatus.label}
              description={ticketStatus.description}
              ariaDescription={ticketStatus.ariaDescription}
              title={revokeReasonTitle}
              className="text-end text-text-700"
            />
          </div>
        </div>
      </Button>
      <div className="flex size-full">
        {canCancelTicket(ticket) ? (
          <span
            className={cn(
              'absolute right-3 top-2 inline-flex @2xl:top-4',
              periodCancel.locked && 'cursor-not-allowed',
            )}
            data-tooltip-id={periodCancel.explain ? cancelTooltipId : undefined}
            data-tooltip-content={
              periodCancel.explain ? cancelReason : undefined
            }
          >
            {periodCancel.explain ? (
              <>
                {createPortal(
                  <Tooltip
                    id={cancelTooltipId}
                    place="top-end"
                    delayShow={300}
                    delayHide={1}
                    positionStrategy="fixed"
                    className="z-50 max-w-sm"
                    style={periodCancelTooltipStyle}
                  />,
                  document.body,
                )}
                <span id={cancelDescriptionId} className="sr-only">
                  {cancelReason}
                </span>
              </>
            ) : null}
            <IconButton
              aria-label={formatMessage({ defaultMessage: 'Avsluta biljett' })}
              aria-describedby={
                periodCancel.explain ? cancelDescriptionId : undefined
              }
              aria-busy={periodCancel.pending || undefined}
              variant="ghost"
              color="red"
              icon={<IconTrash />}
              onClick={startCancel}
              disabled={periodCancel.locked}
              className={
                periodCancel.locked ? 'disabled:opacity-40' : undefined
              }
            />
          </span>
        ) : null}
      </div>
      <CancelIssuedTicketDialog
        open={cancelOpen}
        description={
          <FormattedMessage
            defaultMessage="Är du säker på att du vill avsluta biljetten för resenären {name}? Köpet återbetalas inte."
            values={{
              name: travellerName,
            }}
          />
        }
        loading={revokeIssuedTicket.isPending}
        onCancel={onCancel}
        onConfirm={onCancelConfirm}
      />
    </div>
  );
};

interface TicketInfoProps {
  value: string;
  description?: string;
  ariaDescription?: string;
  title?: string;
  className?: string;
}

const TicketInfo = ({
  value,
  description,
  ariaDescription,
  title,
  className,
}: TicketInfoProps) => {
  const valueOutput = useMemo(() => {
    if (!value) return;

    return (
      <span className="truncate @2xl:w-full" title={title}>
        {value}
      </span>
    );
  }, [title, value]);

  const descriptionOutput = useMemo(() => {
    if (!description) return;

    if (ariaDescription && ariaDescription !== description) {
      return (
        <>
          <span
            aria-hidden="true"
            className="truncate font-light text-text-700 @2xl:w-full"
          >
            {description}
          </span>
          <span className="sr-only">{ariaDescription}</span>
        </>
      );
    }

    return (
      <span className="truncate font-light text-text-700 @2xl:w-full">
        {description}
      </span>
    );
  }, [description, ariaDescription]);

  return (
    <div className={cn('flex w-auto flex-col', className)}>
      {valueOutput}
      {title ? <span className="sr-only">{title}</span> : null}
      {description ? <span className="sr-only">, </span> : null}
      {descriptionOutput}
    </div>
  );
};
